<script lang="ts">
  import { onMount } from 'svelte';
  import { forecastApi } from '../lib/api';
  import type { ForecastData, ForecastParams, TimeSeriesData, ForecastPoint, DataPoint } from '../lib/types';
  import { t } from '../lib/i18n';
  import { generateColorPalette } from '../lib/utils';
  import { toString } from '../lib/id-utils';

  // No default export in Svelte components
  export let timeseriesId: string | number;
  export let timeseriesData: TimeSeriesData | null = null;
  export let height: number = 400;

  // Converted ID for consistent handling - we use toString for timeseries API but the
  // forecast API will use the toUuidFormat conversion internally when needed
  $: stringId = toString(timeseriesId);
  
  // Local state
  let isLoading = false;
  let error = '';
  let forecasts: ForecastData[] = [];
  let forecastPoints: Record<string, ForecastPoint[]> = {};
  let modelForecasts: {id: string, name: string, algorithm: string, points: ForecastPoint[], color: string}[] = [];
  let selectedAlgorithms: string[] = ['arima', 'sarima', 'ets'];
  let forecastHorizon = 14;
  let confidenceLevel = 0.95;
  let showConfidenceIntervals = true;
  let isForecastCreated = false;
  
  // Default parameters for each algorithm
  const arimaParams = { p: 1, d: 1, q: 0 };
  const sarimaParams = { p: 1, d: 1, q: 0, P: 1, D: 0, Q: 0, m: 7 };
  const etsParams = { trend: 'add', seasonal: null, seasonal_periods: 7 };
  
  // Algorithm display names
  const algorithmLabels = {
    'arima': 'ARIMA',
    'sarima': 'SARIMA',
    'ets': 'Экспоненциальное сглаживание (ETS)'
  };
  
  // Helper function to get the display name for an algorithm
  function getAlgorithmLabel(algorithm: string): string {
    return algorithmLabels[algorithm as keyof typeof algorithmLabels] || algorithm;
  }
  
  // Helper function to get the color for a forecast algorithm
  function getForcastColor(algorithm: string): string {
    const forecast = modelForecasts.find(m => m.algorithm === algorithm);
    return forecast?.color || '#ccc';
  }
  
  // Check if data is available
  $: dataAvailable = timeseriesData && timeseriesData.data_points && timeseriesData.data_points.length > 0;
  
  // Process the time series data for the chart
  $: actualData = dataAvailable ? timeseriesData!.data_points as DataPoint[] : [];
  
  // Generate color palette for forecasts
  $: colorPalette = generateColorPalette(selectedAlgorithms.length);
  
  // Type-safe helper function to create a forecast model
  function createForecastModel(algorithm: string, index: number): {
    id: string;
    name: string;
    algorithm: string;
    points: ForecastPoint[];
    color: string;
  } {
    // Get forecast points for this algorithm
    const points = forecastPoints[algorithm] || [];
    // Get color from palette with fallback - ensuring it's always a string
    const color: string = index < colorPalette.length && colorPalette[index] ? colorPalette[index] : '#ccc';
    
    // Return object with display information
    return {
      id: algorithm,
      name: getAlgorithmLabel(algorithm),
      algorithm,
      points,
      color
    };
  }
  
  // Process the forecasts for display
  $: {
    if (isForecastCreated && Object.keys(forecastPoints).length > 0) {
      modelForecasts = selectedAlgorithms.map((algorithm, index) => 
        createForecastModel(algorithm, index)
      );
    } else {
      modelForecasts = [];
    }
  }
  
  /**
   * Create forecasts for all selected algorithms
   */
  async function createAllForecasts() {
    if (!dataAvailable) {
      error = 'No data available to create forecasts';
      return;
    }
    
    try {
      isLoading = true;
      error = '';
      isForecastCreated = false;
      forecastPoints = {};
      
      // Create array of promises for all forecasts
      const forecastPromises = selectedAlgorithms.map(algorithm => {
        // Select appropriate parameters for each algorithm
        let parameters = {};
        if (algorithm === 'arima') {
          parameters = arimaParams;
        } else if (algorithm === 'sarima') {
          parameters = sarimaParams;
        } else if (algorithm === 'ets') {
          parameters = etsParams;
        }
        
        // Create forecast params
        const forecastParams: ForecastParams = {
          algorithm,
          horizon: forecastHorizon,
          confidence_level: confidenceLevel,
          parameters
        };
        
        // Return promise for this forecast creation
        return forecastApi.create(stringId, forecastParams);
      });
      
      // Wait for all forecasts to be created
      const newForecasts = await Promise.all(forecastPromises);
      
      // Add new forecasts to list
      forecasts = [...newForecasts, ...forecasts];
      
      // Now fetch details for each forecast to get the points
      const detailPromises = newForecasts.map(forecast => 
        forecastApi.getById(stringId, forecast.id, true)
      );
      
      // Wait for all details to be loaded
      const forecastDetails = await Promise.all(detailPromises);
      
      // Store forecast points by algorithm
      forecastDetails.forEach(detail => {
        if (detail.forecast_points && detail.forecast_points.length > 0) {
          forecastPoints[detail.algorithm] = detail.forecast_points;
        }
      });
      
      // Mark forecasts as created
      isForecastCreated = true;
      
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to create forecasts';
      console.error('Error creating forecasts:', err);
    } finally {
      isLoading = false;
    }
  }
  
  /**
   * Clear all created forecasts
   */
  function clearForecasts() {
    modelForecasts = [];
    isForecastCreated = false;
    forecastPoints = {};
  }
  
  // When algorithm selection changes, update the color palette
  function updateAlgorithmSelection(algorithm: string, event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.checked) {
      if (!selectedAlgorithms.includes(algorithm)) {
        selectedAlgorithms = [...selectedAlgorithms, algorithm];
      }
    } else {
      selectedAlgorithms = selectedAlgorithms.filter(a => a !== algorithm);
    }
  }
</script>

<div class="multi-forecasts card mb-4">
  <div class="card-header d-flex justify-content-between align-items-center">
    <h5 class="mb-0">{t('Сравнение прогнозов')}</h5>
  </div>
  
  <div class="card-body">
    {#if error}
      <div class="alert alert-danger alert-dismissible fade show" role="alert">
        {error}
        <button type="button" class="btn-close" on:click={() => error = ''} aria-label="Close"></button>
      </div>
    {/if}
    
    <div class="row mb-4">
      <div class="col-md-12">
        <div class="forecast-options p-3 border rounded mb-3">
          <h6>{t('Настройки прогнозирования')}</h6>
          
          <div class="row g-3">
            <div class="col-md-6">
              <div class="algorithm-selection mb-3">
                <div id="algorithm-selection-label">{t('Алгоритмы прогнозирования')}:</div>
                <div class="form-check" aria-labelledby="algorithm-selection-label">
                  <input class="form-check-input" type="checkbox" id="arima" 
                    checked={selectedAlgorithms.includes('arima')}
                    on:change={(event) => updateAlgorithmSelection('arima', event)}
                    disabled={isLoading} />
                  <label class="form-check-label" for="arima">
                    ARIMA (Авторегрессионное интегрированное скользящее среднее)
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" id="sarima" 
                    checked={selectedAlgorithms.includes('sarima')}
                    on:change={(event) => updateAlgorithmSelection('sarima', event)}
                    disabled={isLoading} />
                  <label class="form-check-label" for="sarima">
                    SARIMA (Сезонное ARIMA)
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" id="ets" 
                    checked={selectedAlgorithms.includes('ets')}
                    on:change={(event) => updateAlgorithmSelection('ets', event)}
                    disabled={isLoading} />
                  <label class="form-check-label" for="ets">
                    Экспоненциальное сглаживание (ETS)
                  </label>
                </div>
              </div>
            </div>
            
            <div class="col-md-6">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label for="horizon" class="form-label">{t('Горизонт прогноза (периоды)')}</label>
                    <input type="number" id="horizon" class="form-control" 
                      bind:value={forecastHorizon} min="1" max="365" disabled={isLoading} />
                    <div class="form-text">{t('Количество периодов для прогнозирования')}</div>
                  </div>
                </div>
                
                <div class="col-md-6">
                  <div class="mb-3">
                    <label for="confidence" class="form-label">{t('Уровень доверия')}</label>
                    <select id="confidence" class="form-select" bind:value={confidenceLevel} disabled={isLoading}>
                      <option value="0.8">80%</option>
                      <option value="0.9">90%</option>
                      <option value="0.95">95%</option>
                      <option value="0.99">99%</option>
                    </select>
                  </div>
                </div>
                
                <div class="col-md-12">
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" id="showConfidenceIntervals" 
                      bind:checked={showConfidenceIntervals} disabled={isLoading} />
                    <label class="form-check-label" for="showConfidenceIntervals">
                      {t('Показывать доверительные интервалы')}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="d-flex justify-content-end mt-3">
            {#if !isForecastCreated}
              <button type="button" class="btn btn-primary" on:click={createAllForecasts} disabled={isLoading || !dataAvailable || selectedAlgorithms.length === 0}>
                {#if isLoading}
                  <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {t('Создание прогнозов...')}
                {:else}
                  <i class="bi bi-graph-up me-2"></i>
                  {t('Создать прогнозы всех моделей')}
                {/if}
              </button>
            {:else}
              <button type="button" class="btn btn-outline-secondary me-2" on:click={clearForecasts} disabled={isLoading}>
                <i class="bi bi-x-circle me-2"></i>
                {t('Очистить прогнозы')}
              </button>
              <button type="button" class="btn btn-primary" on:click={createAllForecasts} disabled={isLoading || !dataAvailable || selectedAlgorithms.length === 0}>
                <i class="bi bi-arrow-clockwise me-2"></i>
                {t('Обновить прогнозы')}
              </button>
            {/if}
          </div>
        </div>
      </div>
    </div>
    
    <!-- Chart section -->
    <div class="chart-section">
      {#if dataAvailable}
        {#if isForecastCreated && modelForecasts.length > 0}
          <!-- Modified TimeSeriesChart component that can handle multiple forecasts -->
          <div class="multi-forecast-chart-wrapper" style="height: {height}px;">
            <svelte:component 
              this={MultiTimeSeriesChart}
              actualData={actualData}
              forecasts={modelForecasts}
              showConfidenceIntervals={showConfidenceIntervals}
              height={height}
            />
          </div>
          
          <!-- Legend for the chart -->
          <div class="forecast-legend mt-3 p-3 border rounded">
            <h6>{t('Легенда')}</h6>
            <div class="d-flex flex-wrap">
              <div class="me-4 mb-2">
                <span class="legend-color" style="background-color: #4361ee;"></span>
                <span>{t('Исходные данные')}</span>
              </div>
              
              {#each modelForecasts as forecast}
                <div class="me-4 mb-2">
                  <span class="legend-color" style="background-color: {forecast.color || '#ccc'};"></span>
                  <span>{forecast.name}</span>
                </div>
              {/each}
            </div>
          </div>
          
          <!-- Results table -->
          <div class="forecast-results mt-4">
            <h6>{t('Результаты прогнозирования')}</h6>
            <div class="table-responsive">
              <table class="table table-bordered table-hover">
                <thead>
                  <tr>
                    <th>{t('Алгоритм')}</th>
                    <th>{t('Метрики')}</th>
                    <th>{t('Параметры')}</th>
                    <th>{t('Горизонт')}</th>
                  </tr>
                </thead>
                <tbody>
                  {#each forecasts.filter(f => selectedAlgorithms.includes(f.algorithm)) as forecast}
                    <tr>
                      <td>
                        <div class="d-flex align-items-center">
                          <span class="legend-color me-2" style="background-color: {getForcastColor(forecast.algorithm)};"></span>
                          <div>
                            <div class="fw-bold text-capitalize">{getAlgorithmLabel(forecast.algorithm)}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {#if forecast.metrics && Object.keys(forecast.metrics).length > 0}
                          <div class="metrics-list">
                            {#each Object.entries(forecast.metrics) as [key, value]}
                              <div><span class="text-uppercase fw-bold">{key}:</span> {typeof value === 'number' ? value.toFixed(4) : value}</div>
                            {/each}
                          </div>
                        {:else}
                          <span class="text-muted">{t('Нет данных')}</span>
                        {/if}
                      </td>
                      <td>
                        {#if forecast.parameters}
                          <div class="parameters-list">
                            {#each Object.entries(forecast.parameters) as [key, value]}
                              <div><span class="fw-bold">{key}:</span> {value}</div>
                            {/each}
                          </div>
                        {:else}
                          <span class="text-muted">{t('Нет данных')}</span>
                        {/if}
                      </td>
                      <td>
                        {new Date(forecast.end_date).getTime() - new Date(forecast.start_date).getTime() > 0
                          ? Math.ceil((new Date(forecast.end_date).getTime() - new Date(forecast.start_date).getTime()) / (1000 * 60 * 60 * 24)) + ' дней'
                          : 'Неизвестно'}
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          </div>
        {:else if isLoading}
          <div class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3">{t('Создание прогнозов...')}</p>
          </div>
        {:else}
          <div class="text-center py-5">
            <i class="bi bi-graph-up" style="font-size: 3rem; color: #adb5bd;"></i>
            <p class="mt-3">{t('Нажмите кнопку "Создать прогнозы всех моделей" для сравнения алгоритмов')}</p>
          </div>
        {/if}
      {:else}
        <div class="text-center py-5">
          <i class="bi bi-exclamation-triangle" style="font-size: 3rem; color: #adb5bd;"></i>
          <p class="mt-3">{t('Нет данных для создания прогнозов')}</p>
          <p>{t('Загрузите данные временного ряда на вкладке "Данные"')}</p>
        </div>
      {/if}
    </div>
  </div>
</div>

<!-- Компонент для отображения нескольких прогнозов на одном графике -->
<script context="module" lang="ts">
  import { onMount as mountComponent, afterUpdate } from 'svelte';
  import * as d3 from 'd3';
  import { prepareChartData as prepareData } from '../lib/utils';
  // Use existing DataPoint and ForecastPoint types from main script
  
  export interface MultiModelForecast {
    id: string;
    name: string;
    algorithm: string;
    points: ForecastPoint[];
    color: string;
  }
  
  export function MultiTimeSeriesChart(node: HTMLElement, props: {
    actualData: DataPoint[];
    forecasts: MultiModelForecast[];
    showConfidenceIntervals: boolean;
    height: number;
    animated?: boolean;
    title?: string;
  }) {
    // Initialize variables
    let { actualData, forecasts, showConfidenceIntervals, height, animated = true, title = '' } = props;
    let svg: d3.Selection<SVGSVGElement, unknown, null, undefined>;
    let tooltip: d3.Selection<HTMLDivElement, unknown, null, undefined>;
    let width: number;
    let previousData = JSON.stringify({ actual: actualData, forecasts });
    
    // Create chart when the component is mounted
    mountComponent(() => {
      createChart();
      
      // Handle resize
      const resizeObserver = new ResizeObserver(() => {
        createChart();
      });
      
      resizeObserver.observe(node);
      
      return () => {
        resizeObserver.disconnect();
      };
    });
    
    // Update chart when data changes
    afterUpdate(() => {
      // Only update if data has changed
      const currentData = JSON.stringify({ actual: actualData, forecasts });
      if (currentData !== previousData) {
        previousData = currentData;
        if (svg) {
          updateChart();
        }
      }
    });
    
    // Function to create/update the chart
    function createChart() {
      if (!node) return;
      
      // Clear existing chart
      d3.select(node).selectAll('*').remove();
      
      // Get container width
      width = node.clientWidth;
      
      // Create SVG
      svg = d3.select(node)
        .append('svg')
        .attr('width', width)
        .attr('height', height);
      
      // Create tooltip
      tooltip = d3.select(node)
        .append('div')
        .attr('class', 'chart-tooltip')
        .style('opacity', 0)
        .style('position', 'absolute')
        .style('background-color', 'white')
        .style('border', '1px solid #ddd')
        .style('border-radius', '4px')
        .style('padding', '8px')
        .style('pointer-events', 'none')
        .style('box-shadow', '0 4px 8px rgba(0,0,0,0.1)')
        .style('z-index', '10');
      
      // Update the chart
      updateChart();
    }
    
    // Function to update the chart with new data
    function updateChart() {
      if (!svg || !node) return;
      
      // First prepare the actual data
      const chartData = prepareData(actualData, []);
      if (chartData.data.length === 0 && forecasts.length === 0) return;
      
      // Calculate margins
      const margin = { top: 30, right: 50, bottom: 40, left: 60 };
      const innerWidth = width - margin.left - margin.right;
      const innerHeight = height - margin.top - margin.bottom;
      
      // Combine all data points to determine domain
      let allPoints = [...chartData.data];
      forecasts.forEach(forecast => {
        // Convert forecast points to chart points with color
        const forecastChartPoints = forecast.points.map(point => ({
          timestamp: point.timestamp instanceof Date ? point.timestamp : new Date(point.timestamp),
          value: point.value,
          type: 'forecast',
          algorithm: forecast.algorithm,
          color: forecast.color,
          lower: point.lower,
          upper: point.upper
        }));
        
        allPoints = [...allPoints, ...forecastChartPoints];
      });
      
      // If no points, show message
      if (allPoints.length === 0) {
        svg.append('text')
          .attr('x', width / 2)
          .attr('y', height / 2)
          .attr('text-anchor', 'middle')
          .text('No data available to display');
        return;
      }
      
      // Calculate X domain from all points
      const timestamps = allPoints.map(d => d.timestamp instanceof Date ? d.timestamp : new Date(d.timestamp));
      const minTime = Math.min(...timestamps.map(d => d.getTime()));
      const maxTime = Math.max(...timestamps.map(d => d.getTime()));
      
      // Add 5% padding to x domain
      const timeRange = maxTime - minTime;
      const timePadding = timeRange * 0.05;
      
      const xDomain = [
        new Date(minTime - timePadding),
        new Date(maxTime + timePadding)
      ];
      
      // Calculate Y domain including confidence intervals
      let minValue = d3.min(allPoints, d => {
        const point = d as {value: number; lower?: number; upper?: number};
        return point.lower !== undefined ? point.lower : point.value;
      }) || 0;

      let maxValue = d3.max(allPoints, d => {
        const point = d as {value: number; lower?: number; upper?: number};
        return point.upper !== undefined ? point.upper : point.value;
      }) || 100;
      
      // Add padding to y domain (10%)
      const padding = (maxValue - minValue) * 0.1;
      minValue = Math.max(0, minValue - padding); // Don't go below zero unless data is negative
      maxValue = maxValue + padding;
      
      // Create scales
      const xScale = d3.scaleTime()
        .domain(xDomain)
        .range([0, innerWidth]);
      
      const yScale = d3.scaleLinear()
        .domain([minValue, maxValue])
        .range([innerHeight, 0]);
      
      // Create line generators
      const line = d3.line()
        .x((d: any) => xScale(d.timestamp instanceof Date ? d.timestamp : new Date(d.timestamp)))
        .y((d: any) => yScale(d.value))
        .curve(d3.curveMonotoneX);
      
      // Create chart group
      const chart = svg.append('g')
        .attr('class', 'chart-group')
        .attr('transform', `translate(${margin.left},${margin.top})`);
      
      // Add title if provided
      if (title) {
        chart.append('text')
          .attr('class', 'chart-title')
          .attr('x', innerWidth / 2)
          .attr('y', -10)
          .attr('text-anchor', 'middle')
          .attr('font-weight', 'bold')
          .text(title);
      }
      
      // Add gridlines
      chart.append('g')
        .attr('class', 'grid-y')
        .call(d3.axisLeft(yScale)
          .tickSize(-innerWidth)
          .tickFormat(() => '')
        )
        .selectAll('line')
        .attr('stroke', '#e0e0e0')
        .attr('stroke-opacity', 0.7);
      
      chart.append('g')
        .attr('class', 'grid-x')
        .attr('transform', `translate(0,${innerHeight})`)
        .call(d3.axisBottom(xScale)
          .tickSize(-innerHeight)
          .tickFormat(() => '')
        )
        .selectAll('line')
        .attr('stroke', '#e0e0e0')
        .attr('stroke-opacity', 0.7);
      
      // Add X and Y axes
      chart.append('g')
        .attr('class', 'x-axis')
        .attr('transform', `translate(0,${innerHeight})`)
        .call(d3.axisBottom(xScale));
      
      chart.append('g')
        .attr('class', 'y-axis')
        .call(d3.axisLeft(yScale));
      
      // X axis label
      chart.append('text')
        .attr('class', 'x-axis-label')
        .attr('x', innerWidth / 2)
        .attr('y', innerHeight + 35)
        .attr('text-anchor', 'middle')
        .text('Time');
      
      // Y axis label
      chart.append('text')
        .attr('class', 'y-axis-label')
        .attr('transform', 'rotate(-90)')
        .attr('x', -innerHeight / 2)
        .attr('y', -40)
        .attr('text-anchor', 'middle')
        .text('Value');
      
      // Draw confidence intervals for each forecast if enabled
      if (showConfidenceIntervals) {
        forecasts.forEach(forecast => {
          const confPoints = forecast.points.filter(p => p.lower !== undefined && p.upper !== undefined);
          
          if (confPoints.length > 0) {
            const confidenceArea = d3.area<{timestamp: Date | string; value: number; lower?: number; upper?: number}>()
              .x(d => xScale(d.timestamp instanceof Date ? d.timestamp : new Date(d.timestamp)))
              .y0(d => yScale(d.lower !== undefined ? d.lower : d.value))
              .y1(d => yScale(d.upper !== undefined ? d.upper : d.value))
              .curve(d3.curveMonotoneX);
            
            chart.append('path')
              .attr('class', `confidence-area confidence-${forecast.id}`)
              .datum(confPoints)
              .attr('fill', `${forecast.color}20`) // 20 is hex for 12% opacity
              .attr('d', confidenceArea);
          }
        });
      }
      
      // Draw actual data line
      if (chartData.data.length > 0) {
        const actualPoints = chartData.data.filter(d => d.type === 'actual');
        
        const actualPath = chart.append('path')
          .attr('class', 'actual-line')
          .datum(actualPoints)
          .attr('fill', 'none')
          .attr('stroke', '#4361ee')
          .attr('stroke-width', 2)
          .attr('d', line);
        
        // Add animation if enabled
        if (animated) {
          const pathLength = actualPath.node()?.getTotalLength() || 0;
          actualPath
            .attr('stroke-dasharray', pathLength)
            .attr('stroke-dashoffset', pathLength)
            .transition()
            .duration(1000)
            .attr('stroke-dashoffset', 0);
        }
        
        // Add data points for actual data
        chart.selectAll('.actual-point')
          .data(actualPoints)
          .join('circle')
          .attr('class', 'actual-point')
          .attr('cx', d => xScale(d.timestamp instanceof Date ? d.timestamp : new Date(d.timestamp)))
          .attr('cy', d => yScale(d.value))
          .attr('r', 3)
          .attr('fill', '#4361ee')
          .on('mouseover', function(this: SVGCircleElement, event: MouseEvent, d: {timestamp: Date | string; value: number; type: string}) {
            d3.select(this).attr('r', 5);
            tooltip.transition().duration(200).style('opacity', 0.9);
            tooltip.html(`
              <div style="font-weight: bold;">Actual Data</div>
              <div>Date: ${d.timestamp instanceof Date ? d.timestamp.toLocaleString() : new Date(d.timestamp).toLocaleString()}</div>
              <div>Value: ${d.value.toFixed(2)}</div>
            `)
              .style('left', (event.pageX + 10) + 'px')
              .style('top', (event.pageY - 28) + 'px');
          })
          .on('mouseout', function(this: SVGCircleElement) {
            d3.select(this).attr('r', 3);
            tooltip.transition().duration(500).style('opacity', 0);
          });
      }
      
      // Draw forecast lines for each model
      forecasts.forEach(forecast => {
        const forecastPoints = forecast.points;
        
        if (forecastPoints.length > 0) {
          const forecastPath = chart.append('path')
            .attr('class', `forecast-line forecast-${forecast.id}`)
            .datum(forecastPoints)
            .attr('fill', 'none')
            .attr('stroke', forecast.color)
            .attr('stroke-width', 2)
            .attr('stroke-dasharray', '5,5')
            .attr('d', line);
          
          // Add animation if enabled
          if (animated) {
            const pathLength = forecastPath.node()?.getTotalLength() || 0;
            forecastPath
              .attr('stroke-dasharray', pathLength)
              .attr('stroke-dashoffset', pathLength)
              .transition()
              .duration(1000)
              .attr('stroke-dashoffset', 0);
          }
          
          // Add data points for forecast data
          chart.selectAll(`.forecast-point-${forecast.id}`)
            .data(forecastPoints)
            .join('circle')
            .attr('class', `forecast-point forecast-point-${forecast.id}`)
            .attr('cx', d => xScale(d.timestamp instanceof Date ? d.timestamp : new Date(d.timestamp)))
            .attr('cy', d => yScale(d.value))
            .attr('r', 3)
            .attr('fill', forecast.color)
            .on('mouseover', function(this: SVGCircleElement, event: MouseEvent, d: {timestamp: Date | string; value: number; lower?: number; upper?: number}) {
              d3.select(this).attr('r', 5);

              let tooltipContent = `
                <div style="font-weight: bold;">${forecast.name}</div>
                <div>Date: ${d.timestamp instanceof Date ? d.timestamp.toLocaleString() : new Date(d.timestamp).toLocaleString()}</div>
                <div>Value: ${d.value.toFixed(2)}</div>
              `;
              
              if (d.lower !== undefined && d.upper !== undefined) {
                tooltipContent += `
                  <div>Lower: ${d.lower.toFixed(2)}</div>
                  <div>Upper: ${d.upper.toFixed(2)}</div>
                `;
              }
              
              tooltip.transition().duration(200).style('opacity', 0.9);
              tooltip.html(tooltipContent)
                .style('left', (event.pageX + 10) + 'px')
                .style('top', (event.pageY - 28) + 'px');
            })
            .on('mouseout', function(this: SVGCircleElement) {
              d3.select(this).attr('r', 3);
              tooltip.transition().duration(500).style('opacity', 0);
            });
        }
      });
    }
    
    // Update props when they change
    return {
      update(newProps: {
        actualData: DataPoint[];
        forecasts: MultiModelForecast[];
        showConfidenceIntervals: boolean;
        height: number;
        animated?: boolean;
        title?: string;
      }) {
        // Update local props
        actualData = newProps.actualData;
        forecasts = newProps.forecasts;
        showConfidenceIntervals = newProps.showConfidenceIntervals;
        height = newProps.height;
        animated = newProps.animated !== undefined ? newProps.animated : animated;
        title = newProps.title !== undefined ? newProps.title : title;
        
        // Update chart
        if (svg) {
          updateChart();
        }
      }
    };
  }
</script>

<style>
  .multi-forecasts {
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
  
  .forecast-options {
    background-color: #f8f9fa;
  }
  
  .legend-color {
    display: inline-block;
    width: 15px;
    height: 15px;
    border-radius: 3px;
    margin-right: 5px;
    vertical-align: middle;
  }
  
  .metrics-list, .parameters-list {
    font-size: 0.85rem;
  }
  
  /* Style overrides for multi-forecast chart */
  :global(.multi-forecast-chart-wrapper) {
    position: relative;
    width: 100%;
  }
  
  :global(.multi-forecast-chart-wrapper .chart-tooltip) {
    position: absolute;
    background-color: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 8px;
    pointer-events: none;
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    z-index: 10;
  }
  
  :global(.multi-forecast-chart-wrapper .x-axis path),
  :global(.multi-forecast-chart-wrapper .y-axis path) {
    stroke: #adb5bd;
  }
  
  :global(.multi-forecast-chart-wrapper .x-axis text),
  :global(.multi-forecast-chart-wrapper .y-axis text) {
    fill: #495057;
    font-size: 12px;
  }
  
  :global(.multi-forecast-chart-wrapper .grid-y line),
  :global(.multi-forecast-chart-wrapper .grid-x line) {
    stroke: #e9ecef;
  }
  
  :global(.multi-forecast-chart-wrapper .x-axis-label),
  :global(.multi-forecast-chart-wrapper .y-axis-label) {
    fill: #495057;
    font-size: 12px;
  }
  
  :global(.multi-forecast-chart-wrapper .chart-title) {
    fill: #212529;
    font-size: 16px;
  }
</style>